import type { CheckoutResponse } from "#shared/types/core/ecommerce/checkout";

// TODO: The docs calls this a "description", but I assume it's just a string.
type Description = string;

interface ShippingOption {
  /**
   * The merchants shipping option identification.
   */
  id: string;

  /**
   * The name of the brand of the option (for example, "Posten" or "PostNord").
   */
  brand: string;

  /**
   * The brand specific product (for example, "Servicepakke" or "Home delivery").
   */
  product: string;

  /**
   * The description of the shipping option.
   */
  description?: Description;

  /**
   * The price of the shipping option.
   */
  price: Money;
}

interface Money {
  /**
   * Value of in minor units. For Norwegian kroner (NOK) that means 1 kr = 100 øre. Example: 499 kr = 49900 øre.
   */
  fractionalDenomination: number;

  /**
   * Three letter ISO-4217 currency code.
   */
  currency: string;
}

interface Address {
  address?: string;
  city?: string;
  country?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  zip?: string;
}

type VippsCheckoutStatus =
  | "SessionStarted"
  | "PaymentInitiated"
  | "PaymentSuccessful"
  | "PaymentFailed"
  | "SessionTerminated"
  | "SessionExpired";

interface VippsCheckoutOptions {
  /**
   * Specifies where to load the iFrame content from. Comes from session creation response
   */
  checkoutFrontendUrl: string;

  /**
   * The ID of the HTML element to contain the Checkout iFrame.
   */
  iFrameContainerId: string;

  /**
   * Token identifying the session. Comes from session creation response.
   */
  token: string;

  /**
   * Can be set to nb, dk, fi, or en. This is optional and will default to 'en' English if not specified
   */
  language?: "nb" | "dk" | "fi" | "en";

  /**
   * Listen to events from Checkout.
   */
  on?: Partial<{
    /**
     * Is triggered when the user selects a shipping option or undefined when shipping option is deselected.
     */
    shipping_option_selected: (data: ShippingOption | undefined) => void;

    /**
     * Is triggered when the total amount changes (for example, when a shipping option is selected).
     *
     * @param data - The new total amount.
     */
    total_amount_changed: (data: Money) => void;

    /**
     * Is triggered upon changes in session status (for example, when payment is started).
     */
    session_status_changed: (data: VippsCheckoutStatus) => void;

    /**
     * Is triggered when a new "delivered to" address is submitted or undefined when removed.
     */
    shipping_address_changed: (data: Address | undefined) => void;

    /**
     * Is triggered when new customer information is submitted or undefined when removed.
     */
    customer_information_changed: (data: Address | undefined) => void;
  }>;
}

interface VippsCheckout {
  redirectToCurrentPageWithToken: (token: string) => void;
}

interface VippsCheckoutApi {
  VippsCheckout: (options: VippsCheckoutOptions) => VippsCheckout;
}

declare global {
  interface Window extends VippsCheckoutApi {}
}

export const VIPPS_IFRAME_CONTAINER_ID = "vipps-checkout-frame-container";

export function useVippsCheckout(ready: Ref<boolean>) {
  const session = useVippsSession();

  const script = useScript(
    {
      src: "https://checkout.vipps.no/vippsCheckoutSDK.js",
      type: "text/javascript",
      id: "vipps-script-loader",
      crossorigin: null,
    },
    {
      trigger: ready,
      use() {
        return {
          checkout(data: CheckoutResponse) {
            session.value = {
              id: data.sessionId,
              status: "SessionStarted",
            };

            return window.VippsCheckout({
              checkoutFrontendUrl: data.paymentCheckoutFrontendUrl,
              language: "nb", // TODO: Check if have to be 'no' instead of 'nb'
              token: data.paymentToken,
              iFrameContainerId: VIPPS_IFRAME_CONTAINER_ID,
              on: {
                session_status_changed(data) {
                  if (session.value) {
                    session.value.status = data;
                  }
                },
              },
            });
          },
        };
      },
    },
  );

  return {
    checkout: script.proxy.checkout,
  };
}

export function useVippsSession() {
  return useCookie<{ id: UUID; status: VippsCheckoutStatus } | null>(
    "vipps-session",
    {
      default: () => null,
    },
  );
}
